import React, { useLayoutEffect, useRef, useState, useMemo, useContext, useEffect } from 'react';
import { Card, CardActionArea, CardMedia, Box, Typography } from '@mui/material';
import { Pets, Person } from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import PlaceholderDragon from '../data/PlaceholderDragon.jpg';
import cardBack from '../data/card-back.jpg';
import GoldenSpiralFrame from '../utilities/GoldenSpiralFrame';
import { getCardStats } from '../utilities';
import { UserContext } from '../contexts/UserContext';

// 🔥 Firebase Storage (v9+ modular)
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

/* =========================
   Beasts of Legend Palette
   ========================= */
const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';
const BLACK_DEEP = '#0a0a0a';

/* =========================
   Rarity Helpers
   ========================= */
const rarityAbbrev = (rarity = '') => {
    const r = rarity.toLowerCase();
    if (r.startsWith('common')) return 'C';
    if (r.startsWith('uncommon')) return 'U';
    if (r === 'rare') return 'R';
    if (r.startsWith('high')) return 'HR';
    if (r.startsWith('super super')) return 'SSR';
    if (r.startsWith('super')) return 'SR';
    if (r.startsWith('legend')) return 'L';
    if (r.startsWith('myth')) return 'M';
    return rarity?.[0]?.toUpperCase() || '?';
};

// Use rarity as an ACCENT color (muted where appropriate)
export const rarityColor = (rarityRaw) => {
    const r = String(rarityRaw || '').toLowerCase();
    if (r === 'common') return '#8fa58f';                // muted green
    if (r === 'uncommon') return '#3f7a46';             // deep green
    if (r === 'rare') return '#7aa3d6';                 // muted blue
    if (r === 'high rare') return '#1d4f91';            // deep blue
    if (r === 'super rare') return '#b68ec9';           // muted purple
    if (r === 'super super rare') return '#5a2b86';     // deep purple
    if (r === 'legendary') return GOLD;                 // gold
    if (r === 'mythic') return '#e6d685';               // pale mythic gold
    return '#a8a8a8';                                   // fallback
};

const isAnimatedRarity = (rarityRaw) => {
    const r = String(rarityRaw || '').toLowerCase();
    return r === 'super super rare' || r === 'legendary' || r === 'mythic';
};

const pulseGlow = keyframes`
  0%   { filter: blur(12px); opacity: 0.45; transform: scale(0.98); }
  50%  { filter: blur(16px); opacity: 0.9;  transform: scale(1.02); }
  100% { filter: blur(12px); opacity: 0.45; transform: scale(0.98); }
`;

const pulseBand = keyframes`
  0%   { box-shadow: 0 0 10px var(--rc-55), 0 0 20px var(--rc-33), inset 0 0 10px var(--rc-22); }
  50%  { box-shadow: 0 0 16px var(--rc-88), 0 0 28px var(--rc-55), inset 0 0 12px var(--rc-33); }
  100% { box-shadow: 0 0 10px var(--rc-55), 0 0 20px var(--rc-33), inset 0 0 10px var(--rc-22); }
`;

const pulseFrame = keyframes`
  0%   { box-shadow: 0 0 25px var(--rc-44), 0 0 45px var(--rc-22), inset 0 0 20px rgba(212,175,55,0.06); }
  50%  { box-shadow: 0 0 35px var(--rc-88), 0 0 55px var(--rc-55), inset 0 0 24px var(--rc-22); }
  100% { box-shadow: 0 0 25px var(--rc-44), 0 0 45px var(--rc-22), inset 0 0 20px rgba(212,175,55,0.06); }
`;

/* =========================
   Image URL helpers (JS only)
   ========================= */

// In-memory session cache so we don't refetch the same URL.
const __imageUrlCache = new Map();

function evoClamp(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 1;
    return Math.max(1, Math.min(3, x));
}

function buildCanonicalPath(cardId, evolutionStage) {
    const evo = evoClamp(evolutionStage);
    return `cards/card-images/${cardId}_evo${evo}.png`;
}

// Order of candidates to try:
// 1) cardDoc.images.evoX (if present)
// 2) cardDoc.image (single string path)
// 3) canonical cards/card-images/${cardId}_evo${evo}.png
// plus fallback to evo1 at the end.
function candidatePaths({ ownedCard, cardDoc }) {
    const evo = evoClamp(ownedCard?.evolutionStage || cardDoc?.evolutionStage || 1);
    const cardId = ownedCard?.cardId || cardDoc?.cardId || cardDoc?.id;

    const arr = [];
    if (cardDoc && cardDoc.images && cardDoc.images[`evo${evo}`]) {
        arr.push(cardDoc.images[`evo${evo}`]);
    }
    if (cardDoc && typeof cardDoc.image === 'string' && cardDoc.image) {
        arr.push(cardDoc.image);
    }
    if (cardId) {
        arr.push(buildCanonicalPath(cardId, evo));
    }
    const dedup = [...new Set(arr)];
    if (cardId) dedup.push(buildCanonicalPath(cardId, 1)); // fallback evo1
    return { list: dedup, cardId };
}

async function getUrlForStoragePath(path) {
    if (__imageUrlCache.has(path)) return __imageUrlCache.get(path);
    const storage = getStorage(); // uses default initialized app
    const url = await getDownloadURL(ref(storage, path));
    __imageUrlCache.set(path, url);
    return url;
}

async function resolveCardImageUrl(ownedCard, cardDoc) {
    const { list, cardId } = candidatePaths({ ownedCard, cardDoc });
    if (!cardId) throw new Error('Missing cardId for image resolution.');

    let lastErr = null;
    for (const p of list) {
        try {
            const url = await getUrlForStoragePath(p);
            return url;
        } catch (e) {
            lastErr = e;
        }
    }
    // If everything failed, throw the last error
    throw lastErr || new Error('Unable to resolve image URL.');
}

/* =========================
   Utilities
   ========================= */
const ClassificationGlyph = ({ classification }) => {
    const isBeast = String(classification).toLowerCase().includes('beast');
    return isBeast ? <Pets fontSize="small" /> : <Person fontSize="small" />;
};

function useFitText(
    { max = 1, min = 0.8, step = 0.02 } = {},
    deps = [],
    enabled = true
) {
    const boxRef = useRef(null);
    const textRef = useRef(null);
    const [size, setSize] = useState(max);

    useLayoutEffect(() => {
        if (!enabled) {
            // Lock to max when disabled
            setSize(max);
            return;
        }

        const box = boxRef.current;
        const text = textRef.current;
        if (!box || !text) return;

        const adjust = () => {
            let current = max;
            text.style.fontSize = `${current}rem`;

            const fits = () => text.scrollWidth <= box.clientWidth;
            while (!fits() && current > min) {
                current = Math.max(min, +(current - step).toFixed(3));
                text.style.fontSize = `${current}rem`;
            }
            setSize(current);
        };

        adjust();
        const ro = new ResizeObserver(() => adjust());
        ro.observe(box);
        const timeout = setTimeout(adjust, 0);

        return () => {
            ro.disconnect();
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [max, min, step, enabled, ...deps]);

    return { boxRef, textRef, fontSizeRem: size };
}

/* =========================
   Component
   ========================= */
export function PlayingCard({
    card,
    size = 'md',
    onClick,
    showStats = false,              // default hidden
    showName = true,                // default show name
    showRarityBadge = true,         // default show rarity
    showClassificationBadge = true, // default show man/beast
    showLevel = false,              // default hidden
    wrapName = false,               // allow name wrap/expand
    disableFitText = false,         // allow disabling fit for combat
    imageSrc,                       // NEW: optional resolved image URL from parent
}) {
    const { globalCards } = useContext(UserContext);

    const WIDTHS = {
        sm: 'clamp(140px, 18vw, 180px)',
        md: 'clamp(160px, 20vw, 220px)',
        lg: 'clamp(260px, 40vw, 340px)',
    };
    const width = WIDTHS[size] || WIDTHS.md;

    const INSET_PX = { sm: 12, md: 16, lg: 20 }[size] || 16;
    const BAND_UNITS = { sm: 24, md: 28, lg: 32 }[size] || 28;
    const BADGE_PX = { sm: 24, md: 28, lg: 32 }[size] || 28;

    const NAME_FONT = {
        sm: { max: 0.95, min: 0.8 },
        md: { max: 1.05, min: 0.85 },
        lg: { max: 1.2, min: 0.95 },
    }[size] || { max: 1.05, min: 0.85 };

    const NAME_BAND_MIN_H = { sm: 28, md: 32, lg: 38 }[size] || 32;

    const fitEnabled = !disableFitText;

    const { boxRef, textRef, fontSizeRem } = useFitText(
        NAME_FONT,
        [card?.name, card?.evolutionStage],
        fitEnabled
    );
    const effectiveFontSizeRem = wrapName || !fitEnabled ? NAME_FONT.max : fontSizeRem;

    // number formatter
    const fmt = (n) => Number(n ?? 0).toLocaleString();

    // ======== IMAGE URL RESOLUTION ========
    const [imgUrl, setImgUrl] = useState('');
    const [imgErr, setImgErr] = useState(null);
    const imgUrlRef = useRef('');
    const lastKeyRef = useRef(null);

    // Derive a stable key that represents "which art this card should use"
    const imageKey = useMemo(() => {
        if (!card?.cardId) return null;
        const evo = evoClamp(card?.evolutionStage || 1);
        return `${card.cardId}-evo${evo}`;
    }, [card?.cardId, card?.evolutionStage]);

    useEffect(() => {
        // If parent provided an explicit image URL, skip all Firebase resolution
        if (imageSrc) {
            setImgErr(null);
            setImgUrl('');
            imgUrlRef.current = '';
            lastKeyRef.current = null;
            return;
        }

        if (!imageKey) {
            setImgUrl('');
            setImgErr(null);
            imgUrlRef.current = '';
            lastKeyRef.current = null;
            return;
        }

        // If we've already resolved this key and have a URL, do nothing.
        if (lastKeyRef.current === imageKey && imgUrlRef.current) {
            return;
        }

        lastKeyRef.current = imageKey;
        let alive = true;

        async function run() {
            try {
                setImgErr(null);

                const instance = card || null;
                const base =
                    (card?.cardId && globalCards?.[card.cardId])
                        ? globalCards[card.cardId]
                        : card || null;

                const url = await resolveCardImageUrl(instance, base);

                if (!alive) return;

                if (lastKeyRef.current === imageKey) {
                    if (imgUrlRef.current !== url) {
                        imgUrlRef.current = url;
                        setImgUrl(url);
                    }
                }
            } catch (e) {
                if (!alive) return;

                if (lastKeyRef.current === imageKey) {
                    imgUrlRef.current = '';
                    setImgErr(e);
                    setImgUrl('');
                }
            }
        }

        run();

        return () => {
            alive = false;
        };
        // include imageSrc so we re-run when prop changes / disappears
    }, [imageKey, imageSrc, card, globalCards]);

    // Prefer parent-provided imageSrc, then resolved imgUrl, then cardBack fallback
    const finalImgSrc = imageSrc || imgUrl || cardBack;

    const { atk, def } = useMemo(() => {
        if (typeof card?.attack === 'number' && typeof card?.defense === 'number') {
            return { atk: card.attack, def: card.defense };
        }
        const instance = card || null;
        const base = (card?.cardId && globalCards?.[card.cardId]) ? globalCards[card.cardId] : card || null;
        if (!instance || !base) return { atk: 0, def: 0 };
        try {
            const { attack, defense } = getCardStats(instance, base);
            return { atk: attack ?? 0, def: defense ?? 0 };
        } catch {
            return { atk: 0, def: 0 };
        }
    }, [card, globalCards]);

    const rColor = rarityColor(card?.rarity);
    const animated = isAnimatedRarity(card?.rarity);

    // CSS variables to reuse rarity transparencies in keyframes via Emotion
    const rcVars = {
        '--rc-22': `${rColor}22`,
        '--rc-33': `${rColor}33`,
        '--rc-44': `${rColor}44`,
        '--rc-55': `${rColor}55`,
        '--rc-66': `${rColor}66`,
        '--rc-77': `${rColor}77`,
        '--rc-88': `${rColor}88`,
    };

    return (
        <Box sx={{ width }}>
            {/* NAME BAND with rarity glow */}
            {showName && (
                <Box
                    ref={wrapName ? null : boxRef}
                    sx={{
                        ...rcVars,
                        mb: 0.75,
                        px: 1.25,
                        height: wrapName ? 'auto' : NAME_BAND_MIN_H,
                        minHeight: NAME_BAND_MIN_H,
                        py: wrapName ? 0.75 : 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1.25,
                        border: `1px solid ${rColor}aa`,
                        background: `linear-gradient(180deg, rgba(0,0,0,0.85) 0%, ${BLACK_DEEP} 100%)`,
                        color: TEXT_WARM,
                        boxShadow: `
              0 0 10px ${rColor}55,
              0 0 20px ${rColor}33,
              inset 0 0 10px ${rColor}22
            `,
                        overflow: wrapName ? 'visible' : 'hidden',
                        transition: 'box-shadow 0.4s ease',
                        ...(animated && {
                            animation: `${pulseBand} 3.8s ease-in-out infinite`,
                        }),
                        '&:hover': {
                            boxShadow: `
                0 0 14px ${rColor}88,
                0 0 28px ${rColor}55,
                inset 0 0 10px ${rColor}33
              `,
                        },
                    }}
                >
                    <Typography
                        ref={wrapName ? null : textRef}
                        component="div"
                        sx={{
                            m: 0,
                            p: 0,
                            fontWeight: 800,
                            fontSize: `${effectiveFontSizeRem}rem`,
                            lineHeight: 1.1,
                            whiteSpace: wrapName ? 'normal' : 'nowrap',
                            overflow: wrapName ? 'visible' : 'hidden',
                            textOverflow: wrapName ? 'clip' : 'ellipsis',
                            wordBreak: wrapName ? 'break-word' : 'normal',
                            letterSpacing: effectiveFontSizeRem < NAME_FONT.max ? 0.25 : 0.5,
                            textAlign: 'center',
                            fontFamily: `'Cinzel', serif`,
                            textShadow: `
                0 0 6px ${rColor}88,
                0 0 12px ${rColor}55,
                0 0 18px ${rColor}33
              `,
                        }}
                        title={card?.name}
                    >
                        {card?.name}
                        {card?.evolutionStage === 2 ? ' +' : ''}
                    </Typography>
                </Box>
            )}

            {/* Card with rarity aura + animated pulse for top tiers */}
            <Box sx={{ position: 'relative', width: '100%', aspectRatio: '5/7' }}>
                {/* Outer rarity aura behind card */}
                <Box
                    sx={{
                        ...rcVars,
                        position: 'absolute',
                        inset: '-4%',
                        zIndex: 0,
                        borderRadius: '8px',
                        background: `radial-gradient(circle at center,
              ${rColor}33 0%,
              ${rColor}22 30%,
              transparent 80%)`,
                        filter: 'blur(12px)',
                        opacity: 0.6,
                        pointerEvents: 'none',
                        ...(animated && {
                            animation: `${pulseGlow} 4.5s ease-in-out infinite`,
                        }),
                    }}
                />

                {/* Actual card */}
                <Box sx={{ position: 'absolute', inset: INSET_PX, zIndex: 1, ...rcVars }}>
                    <Card
                        onClick={onClick}
                        sx={{
                            height: '100%',
                            overflow: 'hidden',
                            borderRadius: 2,
                            background: `linear-gradient(145deg, #0b0b0b 0%, #050505 100%)`,
                            border: `1px solid ${rColor}88`,
                            boxShadow: `
                0 0 25px ${rColor}44,
                0 0 45px ${rColor}22,
                inset 0 0 20px rgba(212,175,55,0.06)
              `,
                            transition: 'box-shadow 0.35s ease, transform 0.25s ease',
                            ...(animated && {
                                animation: `${pulseFrame} 5s ease-in-out infinite`,
                            }),
                            '&:hover': {
                                transform: 'translateY(-3px)',
                                boxShadow: `
                  0 0 35px ${rColor}88,
                  0 0 55px ${rColor}55,
                  inset 0 0 25px ${rColor}22
                `,
                            },
                        }}
                    >
                        <CardActionArea sx={{ p: 0, height: '100%', position: 'relative' }}>
                            <CardMedia
                                component="img"
                                src={finalImgSrc}
                                alt={card?.name || 'card art'}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    display: 'block',
                                    backgroundColor: '#050505',
                                    // prevent drag ghost image on desktop
                                    userSelect: 'none',
                                    WebkitUserDrag: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none',
                                }}
                                // If parent gave us imageSrc, we can safely eager-load it
                                loading={imageSrc ? 'eager' : 'lazy'}
                                decoding="async"
                                onError={(e) => {
                                    if (e?.currentTarget?.src !== cardBack) {
                                        e.currentTarget.src = PlaceholderDragon;
                                    }
                                }}
                            />

                            {/* TOP-LEFT CLASSIFICATION BADGE */}
                            {showClassificationBadge && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 6, left: 6,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: BADGE_PX, height: BADGE_PX,
                                        borderRadius: '999px',
                                        background: 'rgba(0,0,0,0.35)',
                                        backdropFilter: 'blur(2px)',
                                        color: GOLD,
                                        boxShadow: `inset 0 0 6px rgba(0,0,0,0.35), 0 0 6px ${GOLD_SOFT}`,
                                        border: `1px solid ${GOLD_DIM}`,
                                    }}
                                >
                                    <ClassificationGlyph classification={card?.classification} />
                                </Box>
                            )}

                            {/* TOP-RIGHT RARITY BADGE */}
                            {showRarityBadge && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 6, right: 6,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        minWidth: BADGE_PX, height: BADGE_PX,
                                        px: 1,
                                        borderRadius: '999px',
                                        typography: 'caption',
                                        fontWeight: 800,
                                        letterSpacing: 0.5,
                                        background: 'rgba(0,0,0,0.35)',
                                        backdropFilter: 'blur(2px)',
                                        color: rColor,
                                        boxShadow: `inset 0 0 6px rgba(0,0,0,0.35), 0 0 8px ${rColor}66`,
                                        border: `1px solid ${rColor}88`,
                                        textTransform: 'uppercase',
                                        fontFamily: `'Cinzel', serif`,
                                    }}
                                >
                                    {rarityAbbrev(card?.rarity)}
                                </Box>
                            )}

                            {/* BOTTOM-RIGHT LEVEL BADGE */}
                            {showLevel && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 6, right: 6,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        minWidth: BADGE_PX, height: BADGE_PX,
                                        px: 1,
                                        borderRadius: '999px',
                                        typography: 'caption',
                                        fontWeight: 800,
                                        letterSpacing: 0.5,
                                        background: 'rgba(0,0,0,0.35)',
                                        backdropFilter: 'blur(2px)',
                                        color: TEXT_WARM,
                                        boxShadow: `inset 0 0 6px rgba(0,0,0,0.35), 0 0 6px ${GOLD_SOFT}`,
                                        border: `1px solid ${GOLD_DIM}`,
                                        fontFamily: `'Cinzel', serif`,
                                    }}
                                >
                                    Lv {card?.level ?? 1}
                                </Box>
                            )}
                        </CardActionArea>
                    </Card>
                </Box>

                {/* Spiral gold border */}
                <Box sx={{ pointerEvents: 'none', position: 'absolute', inset: 0, zIndex: 2 }}>
                    <GoldenSpiralFrame
                        band={BAND_UNITS}
                        rarity={card?.rarity}
                        tintStrength={0.22}
                        tintMode="overlay"
                    />
                </Box>
            </Box>

            {/* ATK / DEF BAR */}
            {showStats && (
                <Box
                    sx={{
                        mt: 0.5,
                        px: 1,
                        py: 0.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderRadius: 1.25,
                        border: `1px solid rgba(212,175,55,0.28)`,
                        background: `linear-gradient(180deg, rgba(0,0,0,0.75) 0%, ${BLACK_DEEP} 100%)`,
                        typography: 'caption',
                        fontWeight: 700,
                        color: TEXT_WARM,
                        boxShadow: `inset 0 0 12px rgba(212,175,55,0.06)`,
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: 0.4,
                    }}
                >
                    <span>ATK: {fmt(atk)}</span>
                    <span>DEF: {fmt(def)}</span>
                </Box>
            )}
        </Box>
    );
}
